import { queryOptions } from '@tanstack/react-query';
import type { ApiResponse } from '@rodzinne-archiwum/shared';

/**
 * Odczyt sesji (§17.1, §17.2, etap 5 zadanie 1 „Sesja").
 *
 * Ciasteczko sesji jest `HttpOnly` (§17.2) — klient NIGDY nie ma do niego dostępu jako do
 * łańcucha znaków. Jedyny sposób, w jaki frontend rozpoznaje zalogowanie, to odpowiedź serwera
 * na `GET /api/auth/get-session`: `200` z ciałem `null`, gdy przeglądarka nie wysłała (albo
 * serwer odrzucił) ciasteczka sesji, albo `200` z `{ user, session }`, gdy sesja jest ważna.
 * Brak sesji jest STANEM NORMALNYM (nie błędem) — funkcja niżej nigdy nie rzuca z tego powodu.
 *
 * Role/uprawnienia NIE są częścią odpowiedzi biblioteki `better-auth` (§11.2: są osobną tabelą
 * łączącą, poza modelem `user`/`session` biblioteki) — dlatego ta sesja jest uzupełniana danymi
 * z `GET /api/v1/me` (`apps/server/src/api/routes/me.ts`, dodane w tym samym zadaniu; patrz
 * uzasadnienie w `apps/server/src/api/schemas/me.ts` i raport zadania) wyłącznie wtedy, gdy
 * `get-session` już potwierdziło, że sesja istnieje — nawigacja filtrowana wg roli (§6) nie ma
 * innego źródła danych.
 */

export interface SessionUser {
  id: string;
  email: string;
  name: string;
}

interface RawSessionResponse {
  user: SessionUser;
  session: { id: string; userId: string; expiresAt: string };
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  status: string;
  /** Kody ról (§4, §11.2) — informacyjne; filtrowanie UI ma używać `permissions`. */
  roles: string[];
  /** Suma efektywnych uprawnień (§4 „efektywne uprawnienia są sumą") — źródło prawdy dla
   * filtrowania nawigacji (§6) i widoczności akcji zależnych od roli. */
  permissions: string[];
}

interface MeDto {
  id: string;
  status: string;
  roles: string[];
  permissions: string[];
}

/**
 * `undefined` = żądanie zawiodło z powodu innego niż „brak sesji" (np. sieć, 5xx) — wywołujący
 * (`fetchCurrentUser`) traktuje to TAK SAMO jak brak sesji: bezpieczny domyślny stan to
 * „niezalogowany", nigdy odwrotnie (§17.1 „bez sesji dostępny jest wyłącznie ekran logowania").
 * `null` = serwer jednoznacznie potwierdził brak sesji (200 z ciałem `null`).
 */
async function fetchBetterAuthSession(signal?: AbortSignal): Promise<RawSessionResponse | null> {
  let response: Response;
  try {
    response = await fetch('/api/auth/get-session', {
      method: 'GET',
      credentials: 'same-origin',
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    return null;
  }
  if (!response.ok) {
    return null;
  }
  const body = (await response.json().catch(() => null)) as RawSessionResponse | null;
  return body;
}

/** Analogiczne bezpieczne traktowanie błędów jak {@link fetchBetterAuthSession} — jeżeli
 * `/api/v1/me` z jakiegokolwiek powodu zawiedzie MIMO potwierdzonej sesji, nawigacja dostaje
 * zero ról/uprawnień (najbardziej restrykcyjny, bezpieczny domyślny stan), a nie awarię UI. */
async function fetchMe(signal?: AbortSignal): Promise<MeDto | null> {
  let response: Response;
  try {
    response = await fetch('/api/v1/me', {
      method: 'GET',
      credentials: 'same-origin',
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    return null;
  }
  if (!response.ok) {
    return null;
  }
  const body = (await response.json().catch(() => null)) as ApiResponse<MeDto> | null;
  return body?.data ?? null;
}

export async function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser | null> {
  const session = await fetchBetterAuthSession(signal);
  if (session === null) {
    return null;
  }

  const me = await fetchMe(signal);

  return {
    id: session.user.id,
    email: session.user.email,
    name: session.user.name,
    status: me?.status ?? 'active',
    roles: me?.roles ?? [],
    permissions: me?.permissions ?? [],
  };
}

/** Klucz zapytania sesji — jedno źródło prawdy, żeby `beforeLoad` trasy chronionej i
 * `AppShell`/`LoginPage` zawsze czytały/unieważniały DOKŁADNIE ten sam wpis w cache. */
export const sessionQueryKey = ['session'] as const;

export function sessionQueryOptions() {
  return queryOptions({
    queryKey: sessionQueryKey,
    queryFn: ({ signal }) => fetchCurrentUser(signal),
  });
}
